import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  User, Mail, Lock, Eye, EyeOff, Pause, Trash2, LogOut, Crown,
  ChevronRight, Camera, GripVertical, Star, Globe, Heart, Calendar,
  Shield, AlertTriangle, Check, X, Loader2, ArrowLeft, RefreshCw
} from 'lucide-react';
import { settingsAPI, profileAPI, swipeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BottomNav } from '../components/BottomNav';
import { toast } from 'sonner';

const LANGUAGES = ['English', 'French', 'Swahili', 'Yoruba', 'Hausa', 'Amharic', 'Arabic', 'Portuguese'];

const COUNTRIES = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Ethiopia', 'Tanzania', 
  'Uganda', 'Senegal', 'Cameroon', 'Ivory Coast', 'Morocco', 'Egypt',
  'USA', 'UK', 'Canada', 'Germany', 'France', 'Australia', 'Netherlands',
  'Italy', 'Spain', 'Sweden', 'Norway', 'Belgium', 'Switzerland'
];

const MEMBERSHIP_TIERS = [
  { id: 'free', name: 'Free', price: '$0', features: ['30 swipes/day', 'Basic matching', 'Limited likes'] },
  { id: 'gold', name: 'Gold', price: '$14.99/mo', features: ['Unlimited swipes', 'See who likes you', '1 Boost/month', 'Read receipts'] },
  { id: 'platinum', name: 'Platinum', price: '$29.99/mo', features: ['Everything in Gold', '5 Super Likes/day', 'Weekly Boost', 'Priority support'] }
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, updateProfile: updateAuthProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [profileData, setProfileData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [photosData, setPhotosData] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [swipeStatus, setSwipeStatus] = useState(null);
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showAgeRangeModal, setShowAgeRangeModal] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  
  // Form states
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [pauseUntil, setPauseUntil] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(60);
  
  // Photo upload
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [replacingPhotoUrl, setReplacingPhotoUrl] = useState(null);

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      const [profileRes, accountRes, photosRes, membershipRes, swipeRes] = await Promise.all([
        settingsAPI.getProfile(),
        settingsAPI.getAccount(),
        settingsAPI.getPhotos(),
        settingsAPI.getMembership(),
        swipeAPI.getStatus()
      ]);
      
      setProfileData(profileRes.data);
      setAccountData(accountRes.data);
      setPhotosData(photosRes.data);
      setMembershipData(membershipRes.data);
      setSwipeStatus(swipeRes.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (field, value) => {
    try {
      await settingsAPI.updateProfile({ [field]: value });
      toast.success('Profile updated');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const handleVisibilityToggle = async () => {
    try {
      const newVisibility = !accountData.is_visible;
      await settingsAPI.setVisibility(newVisibility);
      setAccountData(prev => ({ ...prev, is_visible: newVisibility }));
      toast.success(newVisibility ? 'Profile visible' : 'Profile hidden');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const handlePauseAccount = async () => {
    setSaving(true);
    try {
      if (accountData.is_paused) {
        // Unpause
        await settingsAPI.pauseAccount({ pause_until: null });
        toast.success('Account unpaused');
      } else {
        // Pause
        if (!pauseUntil) {
          toast.error('Please select a date');
          return;
        }
        await settingsAPI.pauseAccount({ pause_until: pauseUntil });
        toast.success('Account paused');
      }
      setShowPauseModal(false);
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause account');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Password required');
      return;
    }
    
    setSaving(true);
    try {
      await settingsAPI.deleteAccount({
        password: deletePassword,
        reason: deleteReason
      });
      toast.success('Account deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill all fields');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    try {
      await settingsAPI.updateAccount({
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!currentPassword || !newEmail) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSaving(true);
    try {
      await settingsAPI.updateAccount({
        current_password: currentPassword,
        email: newEmail
      });
      toast.success('Email changed');
      setShowEmailModal(false);
      setCurrentPassword('');
      setNewEmail('');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change email');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoutAll = async () => {
    try {
      await settingsAPI.logoutAll();
      toast.success('Logged out from all devices');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to logout');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (photosData.photos.length >= photosData.max_photos) {
      toast.error(`Maximum ${photosData.max_photos} photos allowed`);
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await profileAPI.uploadPhoto(formData);
      toast.success('Photo uploaded');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoReorder = async (newOrder) => {
    try {
      await settingsAPI.reorderPhotos(newOrder);
      setPhotosData(prev => ({ ...prev, photos: newOrder }));
    } catch (error) {
      toast.error('Failed to reorder photos');
    }
  };

  const handleSetMainPhoto = async (photoUrl) => {
    try {
      await settingsAPI.setMainPhoto(photoUrl);
      toast.success('Main photo updated');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set main photo');
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    try {
      const filename = photoUrl.split('/').pop();
      await profileAPI.deletePhoto(filename);
      toast.success('Photo deleted');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    }
  };

  const handleReplacePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !replacingPhotoUrl) return;

    setUploadingPhoto(true);
    try {
      // First delete the old photo
      const oldFilename = replacingPhotoUrl.split('/').pop();
      await profileAPI.deletePhoto(oldFilename);
      
      // Then upload the new photo
      const formData = new FormData();
      formData.append('file', file);
      await profileAPI.uploadPhoto(formData);
      
      toast.success('Photo replaced');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace photo');
    } finally {
      setUploadingPhoto(false);
      setReplacingPhotoUrl(null);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const triggerReplacePhoto = (photoUrl) => {
    setReplacingPhotoUrl(photoUrl);
    replaceInputRef.current?.click();
  };

  const handleDowngrade = async () => {
    try {
      await settingsAPI.downgrade();
      toast.success('Downgrade scheduled');
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to downgrade');
    }
  };

  const handleCountryUpdate = async () => {
    if (!selectedCountry) {
      toast.error('Please select a country');
      return;
    }
    setSaving(true);
    try {
      await settingsAPI.updateProfile({ location_country: selectedCountry });
      toast.success('Country updated');
      setShowCountryModal(false);
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update country');
    } finally {
      setSaving(false);
    }
  };

  const handleAgeRangeUpdate = async () => {
    if (ageMin >= ageMax) {
      toast.error('Minimum age must be less than maximum');
      return;
    }
    setSaving(true);
    try {
      await settingsAPI.updateProfile({ 
        age_preference_min: ageMin, 
        age_preference_max: ageMax 
      });
      toast.success('Age range updated');
      setShowAgeRangeModal(false);
      loadAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update age range');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'photos', label: 'Photos', icon: Camera },
    { id: 'membership', label: 'Membership', icon: Crown },
    { id: 'account', label: 'Account', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-dark pb-24" data-testid="settings-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 px-4 py-4">
          <button onClick={() => navigate(-1)} className="p-2 text-gray-400 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-xl text-gold-gradient">Settings</h1>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-body whitespace-nowrap relative ${
                activeTab === tab.id ? 'text-gold' : 'text-gray-400'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {activeTab === tab.id && (
                <motion.div layoutId="settingsTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
              )}
            </button>
          ))}
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* Swipe Limit Indicator */}
        {swipeStatus && !swipeStatus.is_unlimited && (
          <div className="bg-dark-elevated rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm font-body">Daily Swipes</span>
              <span className="text-gold font-body font-semibold">
                {swipeStatus.swipes_remaining}/{swipeStatus.daily_limit} left
              </span>
            </div>
            <div className="h-2 bg-dark rounded-full overflow-hidden">
              <div 
                className="h-full bg-gold transition-all"
                style={{ width: `${(swipeStatus.swipes_remaining / swipeStatus.daily_limit) * 100}%` }}
              />
            </div>
            {swipeStatus.swipes_remaining === 0 && (
              <Button
                onClick={() => navigate('/premium')}
                className="w-full mt-3 btn-gold rounded-full text-sm"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade for Unlimited Swipes
              </Button>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && profileData && (
          <div className="space-y-4">
            <SettingItem
              icon={User}
              label="Bio"
              value={profileData.profile?.bio || 'Not set'}
              onClick={() => {
                const newBio = prompt('Enter your bio:', profileData.profile?.bio || '');
                if (newBio !== null) handleProfileUpdate('bio', newBio);
              }}
            />
            <SettingItem
              icon={Eye}
              label="Show Age"
              value={profileData.profile?.show_age !== false ? 'Yes' : 'No'}
              onClick={() => handleProfileUpdate('show_age', profileData.profile?.show_age === false)}
            />
            <SettingItem
              icon={Heart}
              label="Looking For"
              value={profileData.profile?.seeking || 'Not set'}
              onClick={() => {
                const options = ['men', 'women', 'both'];
                const current = options.indexOf(profileData.profile?.seeking) || 0;
                const next = options[(current + 1) % options.length];
                handleProfileUpdate('seeking', next);
              }}
            />
            <SettingItem
              icon={Globe}
              label="Country"
              value={profileData.profile?.location_country || 'Not set'}
              onClick={() => {
                setSelectedCountry(profileData.profile?.location_country || '');
                setShowCountryModal(true);
              }}
            />
            <SettingItem
              icon={Calendar}
              label="Age Range Preference"
              value={`${profileData.discovery_settings?.age_min || 18} - ${profileData.discovery_settings?.age_max || 60}`}
              onClick={() => {
                setAgeMin(profileData.discovery_settings?.age_min || 18);
                setAgeMax(profileData.discovery_settings?.age_max || 60);
                setShowAgeRangeModal(true);
              }}
            />
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && photosData && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm font-body">
              Drag to reorder. First photo is your main profile picture. Click replace to change a photo.
            </p>
            
            {/* Hidden input for replacing photos */}
            <input
              ref={replaceInputRef}
              type="file"
              accept="image/*"
              onChange={handleReplacePhoto}
              className="hidden"
            />
            
            <Reorder.Group
              axis="y"
              values={photosData.photos}
              onReorder={handlePhotoReorder}
              className="space-y-3"
            >
              {photosData.photos.map((photo, index) => (
                <Reorder.Item key={photo} value={photo}>
                  <div className="bg-dark-elevated rounded-xl p-3 flex items-center gap-3 border border-white/10">
                    <GripVertical className="w-5 h-5 text-gray-500 cursor-grab" />
                    <img
                      src={photo.startsWith('/api') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${photo}`
                        : photo
                      }
                      alt={`Photo ${index + 1}`}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-white font-body text-sm">Photo {index + 1}</p>
                      {index === 0 && (
                        <span className="text-gold text-xs">Main Photo</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => triggerReplacePhoto(photo)}
                        className="p-2 text-gray-400 hover:text-blue-400"
                        title="Replace photo"
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto && replacingPhotoUrl === photo ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </button>
                      {index !== 0 && (
                        <button
                          onClick={() => handleSetMainPhoto(photo)}
                          className="p-2 text-gray-400 hover:text-gold"
                          title="Set as main"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePhoto(photo)}
                        className="p-2 text-gray-400 hover:text-red-400"
                        title="Delete photo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            
            {photosData.photos.length < photosData.max_photos && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="w-full btn-outline border-gold/30 text-gold hover:bg-gold/10"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 mr-2" />
                  )}
                  Add Photo ({photosData.photos.length}/{photosData.max_photos})
                </Button>
              </>
            )}
          </div>
        )}

        {/* Membership Tab */}
        {activeTab === 'membership' && membershipData && (
          <div className="space-y-4">
            {/* Current Plan */}
            <div className="bg-dark-elevated rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${membershipData.is_premium ? 'bg-gold/20' : 'bg-white/5'}`}>
                  <Crown className={`w-6 h-6 ${membershipData.is_premium ? 'text-gold' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-white font-body font-semibold">
                    Current: {membershipData.is_premium ? (membershipData.premium_tier?.charAt(0).toUpperCase() + membershipData.premium_tier?.slice(1)) : 'Free'} Plan
                  </p>
                  {membershipData.premium_expires_at && (
                    <p className="text-gray-400 text-sm">
                      Expires: {new Date(membershipData.premium_expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Membership Tiers */}
            <h3 className="text-gray-400 text-xs uppercase font-body">Choose a Plan</h3>
            <div className="space-y-3">
              {MEMBERSHIP_TIERS.map(tier => {
                const isCurrentPlan = (tier.id === 'free' && !membershipData.is_premium) || 
                                     (membershipData.premium_tier === tier.id);
                return (
                  <div
                    key={tier.id}
                    className={`rounded-xl p-4 border transition-colors ${
                      isCurrentPlan 
                        ? 'bg-gold/10 border-gold' 
                        : 'bg-dark-elevated border-white/10 hover:border-gold/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className={`font-body font-semibold ${isCurrentPlan ? 'text-gold' : 'text-white'}`}>
                          {tier.name}
                        </p>
                        <p className="text-gray-400 text-sm">{tier.price}</p>
                      </div>
                      {isCurrentPlan ? (
                        <span className="px-3 py-1 bg-gold/20 text-gold text-xs rounded-full">Current</span>
                      ) : tier.id !== 'free' ? (
                        <Button
                          onClick={() => navigate('/premium')}
                          size="sm"
                          className="btn-gold text-xs"
                        >
                          Select
                        </Button>
                      ) : null}
                    </div>
                    <ul className="space-y-1">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="text-gray-400 text-xs flex items-center gap-2">
                          <Check className="w-3 h-3 text-gold" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            
            {membershipData.is_premium && (
              <Button
                onClick={handleDowngrade}
                variant="outline"
                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                Cancel Subscription
              </Button>
            )}
            
            {/* Payment History */}
            {membershipData.payment_history?.length > 0 && (
              <div className="bg-dark-elevated rounded-xl p-4 border border-white/10">
                <h3 className="text-white font-body font-semibold mb-3">Payment History</h3>
                <div className="space-y-2">
                  {membershipData.payment_history.slice(0, 5).map((payment, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-400">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </span>
                      <span className={payment.status === 'success' ? 'text-green-400' : 'text-red-400'}>
                        {payment.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && accountData && (
          <div className="space-y-4">
            <SettingItem
              icon={Mail}
              label="Email"
              value={accountData.email}
              onClick={() => {
                setNewEmail(accountData.email);
                setShowEmailModal(true);
              }}
            />
            <SettingItem
              icon={Lock}
              label="Password"
              value="••••••••"
              onClick={() => setShowPasswordModal(true)}
            />
            
            <div className="pt-4 border-t border-white/10">
              <h3 className="text-gray-400 text-xs uppercase font-body mb-3">Privacy</h3>
              
              <div className="bg-dark-elevated rounded-xl p-4 border border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {accountData.is_visible ? (
                    <Eye className="w-5 h-5 text-green-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <p className="text-white font-body text-sm">Profile Visibility</p>
                    <p className="text-gray-400 text-xs">
                      {accountData.is_visible ? 'Visible in discovery' : 'Hidden from discovery'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleVisibilityToggle}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    accountData.is_visible ? 'bg-gold' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    accountData.is_visible ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              <SettingItem
                icon={Pause}
                label="Pause Account"
                value={accountData.is_paused ? `Paused until ${new Date(accountData.pause_until).toLocaleDateString()}` : 'Active'}
                onClick={() => setShowPauseModal(true)}
              />
            </div>
            
            <div className="pt-4 border-t border-white/10">
              <h3 className="text-gray-400 text-xs uppercase font-body mb-3">Security</h3>
              <SettingItem
                icon={LogOut}
                label="Log Out All Devices"
                onClick={handleLogoutAll}
                danger
              />
            </div>
            
            <div className="pt-4 border-t border-white/10">
              <h3 className="text-gray-400 text-xs uppercase font-body mb-3">Danger Zone</h3>
              <SettingItem
                icon={Trash2}
                label="Delete Account"
                onClick={() => setShowDeleteModal(true)}
                danger
              />
            </div>
          </div>
        )}
      </div>

      {/* Delete Account Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="font-heading text-xl text-white mb-2">Delete Account?</h2>
          <p className="text-gray-400 text-sm font-body">
            This will permanently delete your account. Your data will be retained for 30 days before permanent deletion.
          </p>
        </div>
        <Input
          type="password"
          placeholder="Enter your password to confirm"
          value={deletePassword}
          onChange={(e) => setDeletePassword(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white mb-3"
        />
        <Input
          type="text"
          placeholder="Reason for leaving (optional)"
          value={deleteReason}
          onChange={(e) => setDeleteReason(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white mb-4"
        />
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} disabled={saving} className="flex-1 bg-red-500 hover:bg-red-600">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
          </Button>
        </div>
      </Modal>

      {/* Pause Account Modal */}
      <Modal isOpen={showPauseModal} onClose={() => setShowPauseModal(false)}>
        <h2 className="font-heading text-xl text-white mb-4">
          {accountData?.is_paused ? 'Unpause Account' : 'Pause Account'}
        </h2>
        {accountData?.is_paused ? (
          <p className="text-gray-400 text-sm font-body mb-4">
            Your account is paused until {new Date(accountData.pause_until).toLocaleDateString()}.
            Would you like to unpause and become visible again?
          </p>
        ) : (
          <>
            <p className="text-gray-400 text-sm font-body mb-4">
              Pausing your account will hide you from discovery, matches, and chats temporarily.
            </p>
            <Input
              type="date"
              value={pauseUntil}
              onChange={(e) => setPauseUntil(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="bg-dark-elevated border-white/10 text-white mb-4"
            />
          </>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowPauseModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handlePauseAccount} disabled={saving} className="flex-1 btn-gold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (accountData?.is_paused ? 'Unpause' : 'Pause')}
          </Button>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)}>
        <h2 className="font-heading text-xl text-white mb-4">Change Password</h2>
        <Input
          type="password"
          placeholder="Current password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white mb-3"
        />
        <Input
          type="password"
          placeholder="New password (min 8 characters)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white mb-4"
        />
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowPasswordModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleChangePassword} disabled={saving} className="flex-1 btn-gold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change'}
          </Button>
        </div>
      </Modal>

      {/* Change Email Modal */}
      <Modal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)}>
        <h2 className="font-heading text-xl text-white mb-4">Change Email</h2>
        <Input
          type="email"
          placeholder="New email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white mb-3"
        />
        <Input
          type="password"
          placeholder="Current password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white mb-4"
        />
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowEmailModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleChangeEmail} disabled={saving} className="flex-1 btn-gold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change'}
          </Button>
        </div>
      </Modal>

      {/* Country Selection Modal */}
      <Modal isOpen={showCountryModal} onClose={() => setShowCountryModal(false)}>
        <h2 className="font-heading text-xl text-white mb-4">Select Country</h2>
        <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
          {COUNTRIES.map(country => (
            <button
              key={country}
              onClick={() => setSelectedCountry(country)}
              className={`w-full p-3 rounded-xl text-left font-body text-sm transition-colors ${
                selectedCountry === country
                  ? 'bg-gold/20 text-gold border border-gold'
                  : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
              }`}
            >
              {country}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowCountryModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleCountryUpdate} disabled={saving} className="flex-1 btn-gold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </Modal>

      {/* Age Range Modal */}
      <Modal isOpen={showAgeRangeModal} onClose={() => setShowAgeRangeModal(false)}>
        <h2 className="font-heading text-xl text-white mb-4">Age Range Preference</h2>
        <p className="text-gray-400 text-sm font-body mb-4">
          Set the age range of people you want to see in discovery.
        </p>
        <div className="space-y-6 mb-6">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-400 text-sm">Minimum Age</span>
              <span className="text-white font-body">{ageMin}</span>
            </div>
            <input
              type="range"
              min="18"
              max="80"
              value={ageMin}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (val < ageMax) setAgeMin(val);
              }}
              className="w-full accent-gold"
            />
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-400 text-sm">Maximum Age</span>
              <span className="text-white font-body">{ageMax}</span>
            </div>
            <input
              type="range"
              min="18"
              max="80"
              value={ageMax}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (val > ageMin) setAgeMax(val);
              }}
              className="w-full accent-gold"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowAgeRangeModal(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleAgeRangeUpdate} disabled={saving} className="flex-1 btn-gold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </Modal>

      <BottomNav />
    </div>
  );
}

// Helper components
function SettingItem({ icon: Icon, label, value, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className={`w-full bg-dark-elevated rounded-xl p-4 border border-white/10 flex items-center gap-3 text-left hover:bg-white/5 transition-colors ${
        danger ? 'hover:border-red-500/30' : ''
      }`}
    >
      <Icon className={`w-5 h-5 ${danger ? 'text-red-400' : 'text-gray-400'}`} />
      <div className="flex-1">
        <p className={`font-body text-sm ${danger ? 'text-red-400' : 'text-white'}`}>{label}</p>
        {value && <p className="text-gray-400 text-xs">{value}</p>}
      </div>
      <ChevronRight className="w-5 h-5 text-gray-500" />
    </button>
  );
}

function Modal({ isOpen, onClose, children }) {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-dark w-full max-w-md rounded-2xl p-6 border border-white/10"
          onClick={e => e.stopPropagation()}
        >
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
